import { useEffect, useId, useRef, useState } from 'react'
import { ArrowDownIcon, ArrowUpIcon, CornerDownRightIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import type { ContentMenu, ContentMenuItem, ContentSummary } from '@hedgehog/shared'
import { FormError } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { LinkField, linkError, pathForSlug } from './link-field'

interface Item extends ContentMenuItem {
  /** Local id for React keys and focus; not saved. */
  id: string
  children?: Item[]
}

let seq = 0
const withIds = (items: ContentMenuItem[]): Item[] => items.map((i) => ({ ...i, id: `m${++seq}`, children: i.children ? withIds(i.children) : undefined }))
const withoutIds = (items: Item[]): ContentMenuItem[] =>
  items.map(({ id: _id, children, ...i }) => ({ ...i, label: i.label.trim(), url: i.url.trim(), ...(children?.length ? { children: withoutIds(children) } : {}) }))
const same = (a: ContentMenuItem[], b: ContentMenuItem[]) => JSON.stringify(a) === JSON.stringify(b)

/** Path of an item in the tree: [index] or [parent index, child index]. */
type Path = [number] | [number, number]

/**
 * Editor for the main menu: labels, links (a page of the site or another address), order, and
 * items added or removed, with one level of sub-items here (deeper ones are kept as they are).
 * Each item keeps its stored type, reference and attributes; the site turns a page item whose link
 * changed into a custom link. `onSave` stores a draft and resolves to an error message or null.
 */
export function MenuPanel({ menu, pages, onSave }: { menu: ContentMenu | null; pages: ContentSummary[]; onSave: (items: ContentMenuItem[]) => Promise<string | null> }) {
  const id = useId()
  const [items, setItems] = useState<Item[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const focusId = useRef<string | null>(null)
  const box = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (menu) setItems(withIds(menu.items))
  }, [menu])

  useEffect(() => {
    const f = focusId.current
    if (!f) return
    focusId.current = null
    box.current?.querySelector<HTMLElement>(`[data-item="${f}"] input`)?.focus()
  }, [items])

  if (!menu) return <p className="text-sm text-muted-foreground">Loading the menu…</p>

  const saved = withoutIds(withIds(menu.items))
  const dirty = !same(withoutIds(items), saved)

  const update = (path: Path, fn: (list: Item[], i: number) => Item[]) => {
    setError(null)
    setItems((all) => {
      if (path.length === 1) return fn(all, path[0])
      return all.map((p, i) => (i === path[0] ? { ...p, children: fn(p.children ?? [], path[1]) } : p))
    })
  }
  const set = (path: Path, patch: Partial<Item>) => update(path, (list, i) => list.map((x, j) => (j === i ? { ...x, ...patch } : x)))
  const moveBy = (path: Path, d: number) =>
    update(path, (list, i) => {
      const j = i + d
      if (j < 0 || j >= list.length) return list
      const out = [...list]
      ;[out[i], out[j]] = [out[j], out[i]]
      return out
    })
  const remove = (path: Path) => update(path, (list, i) => list.filter((_, j) => j !== i))
  const nextPage = () => {
    const used = new Set(items.flatMap((i) => [i.url, ...(i.children ?? []).map((c) => c.url)]))
    const p = pages.find((x) => !used.has(pathForSlug(x.slug))) ?? pages[0]
    return { label: p?.title ?? 'New page', url: p ? pathForSlug(p.slug) : '/' }
  }
  const add = (parent?: number) => {
    const item: Item = { id: `m${++seq}`, ...nextPage() }
    focusId.current = item.id
    setError(null)
    if (parent === undefined) setItems((all) => [...all, item])
    else setItems((all) => all.map((p, i) => (i === parent ? { ...p, children: [...(p.children ?? []), item] } : p)))
  }

  const invalid = (i: Item) => !i.label.trim() || !!linkError(i.url)
  const anyInvalid = items.some((i) => invalid(i) || (i.children ?? []).some(invalid))

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!dirty) {
      setError('There are no changes to save.')
      return
    }
    setShowErrors(true)
    if (anyInvalid) {
      setError('Some items need a label or a valid link.')
      return
    }
    setBusy(true)
    const err = await onSave(withoutIds(items))
    setBusy(false)
    setError(err)
    if (!err) setShowErrors(false)
  }

  const row = (item: Item, path: Path, count: number) => {
    const key = `${id}-${item.id}`
    const labelError = showErrors && !item.label.trim() ? 'Enter a label.' : null
    const urlError = showErrors ? linkError(item.url) : null
    const name = item.label.trim() || 'this item'
    const index = path[path.length - 1]
    return (
      <li key={item.id} data-item={item.id} className="flex flex-col gap-2 rounded-lg border border-border p-2.5">
        <div className="flex items-end gap-1">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <label htmlFor={`${key}-label`} className="text-sm font-medium">
              {path.length === 2 && <CornerDownRightIcon className="mr-1 inline size-3.5 text-muted-foreground" aria-hidden />}
              Label
            </label>
            <Input id={`${key}-label`} value={item.label} onChange={(e) => set(path, { label: e.target.value })} maxLength={200} aria-invalid={!!labelError || undefined} aria-describedby={labelError ? `${key}-label-error` : undefined} />
          </div>
          <Button type="button" variant="ghost" size="icon-sm" onClick={() => moveBy(path, -1)} disabled={index === 0} aria-label={`Move ${name} up`} title="Move up">
            <ArrowUpIcon aria-hidden />
          </Button>
          <Button type="button" variant="ghost" size="icon-sm" onClick={() => moveBy(path, 1)} disabled={index === count - 1} aria-label={`Move ${name} down`} title="Move down">
            <ArrowDownIcon aria-hidden />
          </Button>
          <Button type="button" variant="ghost" size="icon-sm" onClick={() => remove(path)} aria-label={`Remove ${name} from the menu`} title="Remove">
            <Trash2Icon aria-hidden />
          </Button>
        </div>
        {labelError && (
          <p id={`${key}-label-error`} className="text-xs text-destructive">
            {labelError}
          </p>
        )}
        <LinkField id={`${key}-to`} value={item.url} onChange={(url) => set(path, { url })} pages={pages} error={urlError} />
        {path.length === 1 && (
          <>
            {!!item.children?.length && <ol className="flex flex-col gap-2 border-l-2 border-border pl-3" aria-label={`Sub-items of ${name}`}>{item.children.map((c, j) => row(c, [path[0], j], item.children!.length))}</ol>}
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => add(path[0])} disabled={(item.children?.length ?? 0) >= 50}>
              <PlusIcon aria-hidden />
              Add sub-item
            </Button>
          </>
        )}
      </li>
    )
  }

  return (
    <form ref={box} className="flex flex-col gap-3" onSubmit={save}>
      <p className="text-sm text-muted-foreground">The main menu at the top of every page. Changes stay a draft until you publish.</p>
      {menu.discarded && (
        <p className="rounded-lg bg-muted px-3 py-2 text-sm">An unpublished menu from an earlier visit was removed, because the menu was changed in the classic editor after it.</p>
      )}
      {items.length === 0 && <p className="text-sm">The menu is empty.</p>}
      <ol className="flex flex-col gap-2" aria-label="Menu items">
        {items.map((it, i) => row(it, [i], items.length))}
      </ol>
      <Button type="button" variant="outline" onClick={() => add()} disabled={items.length >= 50}>
        <PlusIcon aria-hidden />
        Add menu item
      </Button>
      <FormError message={error} />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} className="flex-1">
          {busy && <Spinner />}
          Save menu
        </Button>
        <Button type="button" variant="outline" disabled={busy || !dirty} onClick={() => { setItems(withIds(menu.items)); setError(null); setShowErrors(false) }}>
          Undo changes
        </Button>
      </div>
    </form>
  )
}
